import React from "react";

import {
  AddRounded,
  CloseRounded,
  DeleteRounded,
  DirectionsBusRounded,
  SaveRounded,
} from "@mui/icons-material";
import { Box, Chip, DialogContent } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { httpsCallable } from "firebase/functions";
import { DateTime } from "luxon";

import useFirebase from "../../../../../config/context/firebaseContext";
import useModal from "../../../../../config/context/modalContext";
import { GreyPallet } from "../../../../../config/styles";
import { Dev } from "../../../../../config/util/consoleUtil";

import TextMain from "../../../../../components/InputMain/TextMain";
import { WideDialog } from "../../../../../components/MainModalSimple/styles";
import MainSolidButton from "../../../../../components/MainSolidButton";
import { ModalTransition } from "../../../../../components/ModalTransition";
import PageHeader from "../../../../../components/PageHeader";

import SelectMain from "../../../../../components/InputMain/SelectMain";
import { BusSchedulesType, ScheduleRuleInfoType } from "../../../../../types/bus";
import { BannerTypesAvailableType, ModalBaseType } from "../../../../../types/common";

type ModalWriteRuleProps = ModalBaseType & {
  rule?: ScheduleRuleInfoType;
  id?: string;
  schedules: BusSchedulesType;
};

export default function ModalWriteRule({ onClose, rule, id, schedules }: ModalWriteRuleProps) {
  const { pushAlert } = useModal();
  const { functions } = useFirebase();

  const options = Object.keys(schedules).map((key) => ({
    value: key,
    name: schedules[key].name + ` (${key})`,
  }));

  const [open, setOpen] = React.useState(true);

  const [idField, setIdField] = React.useState(id || "");
  const [nameField, setNameField] = React.useState(rule?.name || "");

  const [domField, setDomField] = React.useState(rule?.days[0] || []);
  const [segField, setSegField] = React.useState(rule?.days[1] || []);
  const [terField, setTerField] = React.useState(rule?.days[2] || []);
  const [quaField, setQuaField] = React.useState(rule?.days[3] || []);
  const [quiField, setQuiField] = React.useState(rule?.days[4] || []);
  const [sexField, setSexField] = React.useState(rule?.days[5] || []);
  const [sabField, setSabField] = React.useState(rule?.days[6] || []);

  const [domTextField, setDomTextField] = React.useState("");
  const [segTextField, setSegTextField] = React.useState("");
  const [terTextField, setTerTextField] = React.useState("");
  const [quaTextField, setQuaTextField] = React.useState("");
  const [quiTextField, setQuiTextField] = React.useState("");
  const [sexTextField, setSexTextField] = React.useState("");
  const [sabTextField, setSabTextField] = React.useState("");

  const [startField, setStartField] = React.useState<DateTime | null>(
    rule?.period?.from ? DateTime.fromMillis(rule?.period?.from) : DateTime.now().minus({ day: 2 })
  );
  const [endField, setEndField] = React.useState<DateTime | null>(
    rule?.period?.to ? DateTime.fromMillis(rule?.period?.to) : DateTime.now().minus({ day: 1 })
  );

  const [loading, setLoading] = React.useState(false);
  const [loadingDelete, setLoadingDelete] = React.useState(false);

  const handleWrite = async (operation: "create" | "update" | "delete") => {
    try {
      operation === "delete" ? setLoadingDelete(true) : setLoading(true);
      const writeConfig = httpsCallable(functions, "writeRemoteConfigRules");

      const payload = {
        operation,
        id: id || idField,
        rule: {
          period: {
            from: startField?.toMillis(),
            to: endField?.toMillis(),
          },
          name: nameField,
          days: {
            "0": domField,
            "1": segField,
            "2": terField,
            "3": quaField,
            "4": quiField,
            "5": sexField,
            "6": sabField,
          },
        },
      } as {
        operation: string;
        id: string;
        rule: ScheduleRuleInfoType;
      };

      const response = await writeConfig(payload);

      pushAlert({
        type: "success",
        message: (response?.data as any)?.message || "Configurações atualizadas",
      });

      handleClose(true);
    } catch (error) {
      console.log(...new Dev("handleWrite", error).Error());
      pushAlert({
        type: "error",
        message: `Erro ao salvar dados`,
      });
    } finally {
      setLoading(false);
      setLoadingDelete(false);
    }
  };

  const handleClose = (reload: boolean = false) => {
    setOpen(false);
    setTimeout(() => {
      if (onClose) onClose(reload);
    }, 200);
  };

  const handleDelete = () => {
    pushAlert({
      isConfirm: true,
      type: "delete",
      onConfirm: async () => {
        handleWrite("delete");
      },
    });
  };

  return (
    <WideDialog
      fullWidth={true}
      open={open}
      onClose={() => handleClose()}
      TransitionComponent={ModalTransition}
    >
      <DialogContent>
        <PageHeader
          size="subtitle"
          title={id ? "Editar" : "Criar Regra"}
          rightIcon={<CloseRounded />}
          onRightAction={() => handleClose()}
        />

        <Box>
          <Box>
            <Box>
              <TextMain
                disabled={!!id}
                textColor={"regular"}
                name="ID"
                onChange={setIdField}
                value={idField}
              />
            </Box>

            <Box>
              <TextMain
                textColor={"regular"}
                name="Nome"
                onChange={setNameField}
                value={nameField}
              />
            </Box>

            <Box display={"flex"} justifyContent={"center"} alignItems={"center"} gap={2} mt={2}>
              <DesktopDatePicker
                format="dd/LL/yy"
                orientation="portrait"
                label="Inicio"
                value={startField}
                onChange={(newValue: any) => setStartField(newValue)}
                slotProps={{
                  textField: {
                    InputLabelProps: { shrink: true },
                  },
                }}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root.MuiOutlinedInput-root": {
                    borderRadius: "15px",
                    height: 60,
                    fontWeight: "bold",
                    fontSize: 16,
                    color: GreyPallet.Regular,
                    mb: 2,
                  },
                  "& .MuiFormLabel-root.MuiInputLabel-root": {
                    fontWeight: "bold",
                  },
                  "& .MuiFormLabel-root.MuiInputLabel-root.Mui-focused": {
                    color: GreyPallet.Regular,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "3px !important",
                    borderColor: `${GreyPallet.Light} !important`,
                    borderStyle: "solid",
                    "& .Mui-focused": {
                      borderColor: `${GreyPallet.Regular} !important`,
                    },
                  },
                }}
              />

              <DesktopDatePicker
                minDate={startField?.plus({ day: 1 })}
                format="dd/LL/yy"
                orientation="portrait"
                label="Fim"
                value={endField}
                onChange={(newValue: any) => setEndField(newValue)}
                slotProps={{
                  textField: {
                    InputLabelProps: { shrink: true },
                  },
                }}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root.MuiOutlinedInput-root": {
                    borderRadius: "15px",
                    height: 60,
                    fontWeight: "bold",
                    fontSize: 16,
                    color: GreyPallet.Regular,
                    mb: 2,
                  },
                  "& .MuiFormLabel-root.MuiInputLabel-root": {
                    fontWeight: "bold",
                  },
                  "& .MuiFormLabel-root.MuiInputLabel-root.Mui-focused": {
                    color: GreyPallet.Regular,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "3px !important",
                    borderColor: `${GreyPallet.Light} !important`,
                    borderStyle: "solid",
                    "& .Mui-focused": {
                      borderColor: `${GreyPallet.Regular} !important`,
                    },
                  },
                }}
              />
            </Box>

            {/*  */}

            <Box>
              {domField.map((item) => (
                <Chip
                  key={item}
                  sx={{ width: "100%", mb: 1 }}
                  icon={<DirectionsBusRounded />}
                  label={`${schedules[item].name} (${item})`}
                  onDelete={() => setDomField(domField.filter((key) => key !== item))}
                />
              ))}
              <Box mb={5} display={"flex"} alignItems={"end"} gap={1}>
                <Box flex={1} width={"100%"}>
                  <SelectMain
                    name="Domingo"
                    value={domTextField || ""}
                    options={options}
                    onChange={(value) => setDomTextField(value as BannerTypesAvailableType)}
                  />
                </Box>
                <Box>
                  <MainSolidButton
                    noIconMargin
                    color={"black"}
                    onClick={() => {
                      if (domTextField) {
                        setDomField([...domField, domTextField]);
                        setDomTextField("");
                      }
                    }}
                    icon={<AddRounded />}
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              {segField.map((item) => (
                <Chip
                  key={item}
                  sx={{ width: "100%", mb: 1 }}
                  icon={<DirectionsBusRounded />}
                  label={`${schedules[item].name} (${item})`}
                  onDelete={() => setSegField(segField.filter((key) => key !== item))}
                />
              ))}
              <Box mb={5} display={"flex"} alignItems={"end"} gap={1}>
                <Box flex={1} width={"100%"}>
                  <SelectMain
                    name="Segunda"
                    value={segTextField || ""}
                    options={options}
                    onChange={(value) => setSegTextField(value as BannerTypesAvailableType)}
                  />
                </Box>
                <Box>
                  <MainSolidButton
                    noIconMargin
                    color={"black"}
                    onClick={() => {
                      if (segTextField) {
                        setSegField([...segField, segTextField]);
                        setSegTextField("");
                      }
                    }}
                    icon={<AddRounded />}
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              {terField.map((item) => (
                <Chip
                  key={item}
                  sx={{ width: "100%", mb: 1 }}
                  icon={<DirectionsBusRounded />}
                  label={`${schedules[item].name} (${item})`}
                  onDelete={() => setTerField(terField.filter((key) => key !== item))}
                />
              ))}
              <Box mb={5} display={"flex"} alignItems={"end"} gap={1}>
                <Box flex={1} width={"100%"}>
                  <SelectMain
                    name="Terça"
                    value={terTextField || ""}
                    options={options}
                    onChange={(value) => setTerTextField(value as BannerTypesAvailableType)}
                  />
                </Box>
                <Box>
                  <MainSolidButton
                    noIconMargin
                    color={"black"}
                    onClick={() => {
                      if (terTextField) {
                        setTerField([...terField, terTextField]);
                        setTerTextField("");
                      }
                    }}
                    icon={<AddRounded />}
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              {quaField.map((item) => (
                <Chip
                  key={item}
                  sx={{ width: "100%", mb: 1 }}
                  icon={<DirectionsBusRounded />}
                  label={`${schedules[item].name} (${item})`}
                  onDelete={() => setQuaField(quaField.filter((key) => key !== item))}
                />
              ))}
              <Box mb={5} display={"flex"} alignItems={"end"} gap={1}>
                <Box flex={1} width={"100%"}>
                  <SelectMain
                    name="Quarta"
                    value={quaTextField || ""}
                    options={options}
                    onChange={(value) => setQuaTextField(value as BannerTypesAvailableType)}
                  />
                </Box>
                <Box>
                  <MainSolidButton
                    noIconMargin
                    color={"black"}
                    onClick={() => {
                      if (quaTextField) {
                        setQuaField([...quaField, quaTextField]);
                        setQuaTextField("");
                      }
                    }}
                    icon={<AddRounded />}
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              {quiField.map((item) => (
                <Chip
                  key={item}
                  sx={{ width: "100%", mb: 1 }}
                  icon={<DirectionsBusRounded />}
                  label={`${schedules[item].name} (${item})`}
                  onDelete={() => setQuiField(quiField.filter((key) => key !== item))}
                />
              ))}
              <Box mb={5} display={"flex"} alignItems={"end"} gap={1}>
                <Box flex={1} width={"100%"}>
                  <SelectMain
                    name="Quinta"
                    value={quiTextField || ""}
                    options={options}
                    onChange={(value) => setQuiTextField(value as BannerTypesAvailableType)}
                  />
                </Box>
                <Box>
                  <MainSolidButton
                    noIconMargin
                    color={"black"}
                    onClick={() => {
                      if (quiTextField) {
                        setQuiField([...quiField, quiTextField]);
                        setQuiTextField("");
                      }
                    }}
                    icon={<AddRounded />}
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              {sexField.map((item) => (
                <Chip
                  key={item}
                  sx={{ width: "100%", mb: 1 }}
                  icon={<DirectionsBusRounded />}
                  label={`${schedules[item].name} (${item})`}
                  onDelete={() => setSexField(sexField.filter((key) => key !== item))}
                />
              ))}
              <Box mb={5} display={"flex"} alignItems={"end"} gap={1}>
                <Box flex={1} width={"100%"}>
                  <SelectMain
                    name="Sexta"
                    value={sexTextField || ""}
                    options={options}
                    onChange={(value) => setSexTextField(value as BannerTypesAvailableType)}
                  />
                </Box>
                <Box>
                  <MainSolidButton
                    noIconMargin
                    color={"black"}
                    onClick={() => {
                      if (sexTextField) {
                        setSexField([...sexField, sexTextField]);
                        setSexTextField("");
                      }
                    }}
                    icon={<AddRounded />}
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              {sabField.map((item) => (
                <Chip
                  key={item}
                  sx={{ width: "100%", mb: 1 }}
                  icon={<DirectionsBusRounded />}
                  label={`${schedules[item].name} (${item})`}
                  onDelete={() => setSabField(sabField.filter((key) => key !== item))}
                />
              ))}
              <Box mb={5} display={"flex"} alignItems={"end"} gap={1}>
                <Box flex={1} width={"100%"}>
                  <SelectMain
                    name="Sábado"
                    value={sabTextField || ""}
                    options={options}
                    onChange={(value) => setSabTextField(value as BannerTypesAvailableType)}
                  />
                </Box>
                <Box>
                  <MainSolidButton
                    noIconMargin
                    color={"black"}
                    onClick={() => {
                      if (sabTextField) {
                        setSabField([...sabField, sabTextField]);
                        setSabTextField("");
                      }
                    }}
                    icon={<AddRounded />}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box my={2}>
            <MainSolidButton
              loading={loading}
              text={id ? "Salvar" : "Criar"}
              icon={id ? <SaveRounded /> : <AddRounded />}
              onClick={() => handleWrite(id ? "update" : "create")}
            />
          </Box>

          {!!id && (
            <Box my={2}>
              <MainSolidButton
                color="error"
                loading={loadingDelete}
                text={"Deletar"}
                disabled={loading || loadingDelete}
                icon={<DeleteRounded />}
                onClick={handleDelete}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
    </WideDialog>
  );
}
